// Conversation (chat) endpoints. Separate from responses.js: one is the run,
// this is the history around it.

import { getAuthToken } from './api';

const BASE = '/api/v1/chat/conversations';

async function request(path, options = {}) {
  const token = await getAuthToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE}${path}`, { ...options, headers });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const message =
      body?.error?.Message || body?.error?.message || `request failed (${response.status})`;

    throw new Error(message);
  }

  return response.status === 204 ? null : response.json();
}

// Every chat the user owns, newest activity first. project/bot narrow it; the
// sidebar deliberately doesn't, so switching bot doesn't hide your history.
export async function listConversations({ project, bot, limit } = {}) {
  const params = new URLSearchParams();
  if (project) params.set('project', project);
  if (bot) params.set('bot', bot);
  if (limit) params.set('limit', String(limit));

  const query = params.toString();
  const body = await request(query ? `?${query}` : '');

  return body?.data || [];
}

// The server answers pinned-first and metadata is all strings, so this is the
// one place that turns the flag back into a boolean.
export const isPinned = (chat) => chat?.metadata?.pinned === 'true';

export async function listItems(conversationID) {
  const body = await request(`/${encodeURIComponent(conversationID)}/items`);

  return { items: body?.data || [], headID: body?.head_id || null };
}

export function renameConversation(conversationID, title) {
  return request(`/${encodeURIComponent(conversationID)}`, {
    method: 'POST',
    body: JSON.stringify({ metadata: { title } }),
  });
}

// Pinning keeps a chat at the top of the list and out of the retention sweep.
// metadata is string-valued on the wire, hence the stringified flag.
export function pinConversation(conversationID, pinned) {
  return request(`/${encodeURIComponent(conversationID)}`, {
    method: 'POST',
    body: JSON.stringify({ metadata: { pinned: String(pinned) } }),
  });
}

// Branching is a copy: the source chat is left alone and the caller opens the
// new one. Returns the forked conversation.
export function forkConversation(conversationID, upToItem) {
  return request(`/${encodeURIComponent(conversationID)}/fork`, {
    method: 'POST',
    body: JSON.stringify({ up_to_item: upToItem }),
  });
}

// A fresh link for a file whose last one expired. The id is stable, the link is
// not, which is why nothing stores one.
export async function refreshFileURL(fileID) {
  const token = await getAuthToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const response = await fetch(`/api/v1/files/${encodeURIComponent(fileID)}`, { headers });
  if (!response.ok) {
    throw new Error(`cannot refresh file link (${response.status})`);
  }

  const body = await response.json();

  return body?.url || null;
}

// Creates an empty chat. The normal path lets the first turn create it, so
// this exists for the one case that can't wait: a file has to belong to a
// conversation before it can be uploaded, and the user may attach before they
// type. The chat is titled on the first real message either way.
export function createConversation(project, bot) {
  return request('', {
    method: 'POST',
    body: JSON.stringify({ metadata: { project, bot } }),
  });
}

export function deleteConversation(conversationID) {
  return request(`/${encodeURIComponent(conversationID)}`, { method: 'DELETE' });
}

// filePart maps one stored file onto the assistant-ui part that can show it.
// The url is minted per response by the API, so it is as fresh as the payload
// it arrived in; an expired one is refetched rather than treated as an error.
export function filePart(file) {
  if (isImage(file.media_type)) {
    return {
      type: 'image',
      image: file.url,
      filename: file.filename,
      fileId: file.id,
      // the browser reserves the box from these before a byte arrives, so a
      // picture decoding mid-answer doesn't shove the conversation down
      width: file.width,
      height: file.height,
    };
  }

  return {
    type: 'file',
    data: file.url,
    sourceType: 'url',
    mimeType: file.media_type || 'application/octet-stream',
    filename: file.filename,
    fileId: file.id,
    bytes: file.bytes,
  };
}

export const isImage = (mediaType) => (mediaType || '').startsWith('image/');

// A paused run, as an assistant-ui data part. Not text: the thread renders it
// as a question waiting on the reader rather than as something the model said.
export function interruptPart(interrupt) {
  return {
    type: 'data',
    name: 'interrupt',
    data: {
      kind: interrupt.kind || '',
      message: interrupt.message || '',
      toolName: interrupt.tool_name || '',
      toolCallId: interrupt.tool_call_id || '',
      // undefined while it is still waiting on someone
      approved: interrupt.approved,
    },
  };
}

// The server's parts vocabulary is the one the live stream uses, so history and
// streaming render through the same components.
function partsOf(item) {
  const parts = [];

  for (const part of item.parts || []) {
    switch (part.kind) {
      case 'text':
        parts.push({ type: 'text', text: part.text?.text || '' });
        break;

      case 'reasoning':
        parts.push({ type: 'reasoning', text: part.reasoning?.text || '' });
        break;

      case 'tool_call': {
        const call = part.tool_call || {};
        parts.push({
          type: 'tool-call',
          toolCallId: call.call_id,
          toolName: call.name,
          args: {},
          argsText: call.args || '',
          result: call.result ?? (call.status === 'failed' ? call.error?.message : undefined),
          isError: call.status === 'failed',
        });
        break;
      }

      case 'interrupt':
        parts.push(interruptPart(part.interrupt || {}));
        break;

      case 'attachment': {
        // A user's uploads are lifted into message.attachments by
        // attachmentsOf, so turning them into parts here would show the same
        // file twice, once as a preview it never asked for.
        if (item.role === 'user') break;

        const attached = part.attachment || {};
        const file = attached.ref || {};

        // assistant-ui's own part types rather than a custom one: an image gets
        // the Image slot and everything else the File slot, so the live stream
        // and reloaded history render through the same two components.
        parts.push(filePart(file));
        break;
      }

      default:
        break;
    }
  }

  // a turn that produced nothing still needs a part, or assistant-ui renders
  // an empty bubble with no explanation
  if (parts.length === 0) {
    parts.push({ type: 'text', text: '' });
  }

  return parts;
}

// Into assistant-ui's repository shape: flat messages with parent pointers plus
// the active head, which is exactly how we store them.
//
// metadata is NOT optional, whatever the types suggest: the runtime reads
// mostRecentAssistantMessage?.metadata.unstable_state with the guard on the
// message rather than on metadata, so an imported assistant message without it
// throws as soon as thread state is computed.
export function toRepository({ items, headID }) {
  return {
    headId: headID,
    messages: items.map((item) => ({
      parentId: item.parent_id || null,
      message: messageOf(item),
    })),
  };
}

function messageOf(item) {
  const common = {
    id: item.id,
    createdAt: new Date((item.created_at || 0) * 1000),
    content: partsOf(item),
  };

  // A user's attachments are what they handed us, so they show as file info.
  // The assistant's are things it produced, which is the case a preview is
  // actually for, and those stay as message parts (see partsOf).


  if (item.role === 'assistant') {
    return {
      ...common,
      role: 'assistant',
      status: statusOf(item),
      metadata: {
        unstable_state: null,
        unstable_annotations: [],
        unstable_data: [],
        steps: [],
        custom: {},
      },
    };
  }

  return {
    ...common,
    role: item.role,
    attachments: attachmentsOf(item),
    metadata: { custom: {} },
  };
}

// attachmentsOf lifts the stored attachment parts of a user's turn into the
// shape MessagePrimitive.Attachments renders. content is empty on purpose:
// these are announced, not shown.
function attachmentsOf(item) {
  const attachments = [];

  for (const part of item.parts || []) {
    if (part.kind !== 'attachment') continue;

    const file = part.attachment?.ref || {};
    attachments.push({
      id: file.id,
      fileId: file.id,
      type: isImage(file.media_type) ? 'image' : 'document',
      name: file.filename,
      contentType: file.media_type,
      bytes: file.bytes,
      status: { type: 'complete' },
      content: [],
    });
  }

  return attachments;
}

function statusOf(item) {
  switch (item.status) {
    case 'incomplete:cancelled':
      return { type: 'incomplete', reason: 'cancelled' };
    case 'incomplete:error':
      return {
        type: 'incomplete',
        reason: 'error',
        // the same shape the live stream throws, so a reload shows the same
        error: {
          code: item.error?.code || 'unknown',
          message: item.error?.message || 'the run failed',
          detail: item.error?.detail,
        },
      };
    default:
      return { type: 'complete', reason: 'stop' };
  }
}
